/**
 * Adapter: the flow designer's in-memory canvas state → the `LiveWorkflow`
 * shape that `@chatsdet/canary-spec`'s reverse-renderers consume.
 *
 * The renderers (`materializeWorkflowToBlocks`, `workflowToSpec`) expect the
 * `GET /workflows/:id` shape — nodes as `{ id, nodeType, configJson, orderIndex }`,
 * edges as `{ id, sourceNodeId, targetNodeId, edgeType }`, plus workflow-level
 * metadata (description, inputs, scenarios, …). The designer holds the live
 * nodes/edges as `{ id, type, data }` / `{ id, source, target, edgeType }` and
 * the metadata on the `workflow-detail` query payload. We map the in-memory
 * nodes/edges (not a refetch) so the rendered spec reflects the current canvas.
 *
 * @module FlowDesigner/spec/buildLiveWorkflow
 */

import type { PullWorkflow } from '@chatsdet/canary-spec/browser';
import type { WorkflowEdge, WorkflowNode } from '../../../../types/workflow';

/** The subset of the `workflow-detail` payload the spec render needs. */
export interface SpecWorkflowMeta {
  id: string;
  name?: string | null;
  description?: string | null;
  preflight?: string | null;
  cleanup?: string | null;
  status?: string | null;
  flowType?: string | null;
  apiOnly?: boolean | null;
  inputsJson?: unknown;
  scenariosJson?: unknown;
  seedOutputsJson?: unknown;
  tags?: unknown;
}

export interface BuildLiveWorkflowArgs {
  meta: SpecWorkflowMeta;
  name: string;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
}

/** Coerce the various tag payload shapes into the `{ name }[]` form. */
function normalizeTags(raw: unknown): Array<{ name: string }> {
  if (!Array.isArray(raw)) return [];
  const out: Array<{ name: string }> = [];
  for (const t of raw) {
    if (typeof t === 'string') out.push({ name: t });
    else if (t && typeof t === 'object' && typeof (t as { name?: unknown }).name === 'string') {
      out.push({ name: (t as { name: string }).name });
    }
  }
  return out;
}

export function buildLiveWorkflow({ meta, name, nodes, edges }: BuildLiveWorkflowArgs): PullWorkflow {
  return {
    id: meta.id,
    name: meta.name ?? name,
    description: meta.description ?? null,
    preflight: meta.preflight ?? null,
    cleanup: meta.cleanup ?? null,
    status: meta.status ?? 'draft',
    flowType: meta.flowType ?? 'standard',
    apiOnly: meta.apiOnly ?? undefined,
    inputsJson: meta.inputsJson ?? [],
    scenariosJson: meta.scenariosJson ?? [],
    // `materialize` ignores seedOutputs; `workflowToSpec` (full-fidelity
    // download) renders them as the Declared Outputs section.
    seedOutputs: Array.isArray(meta.seedOutputsJson)
      ? (meta.seedOutputsJson as PullWorkflow['seedOutputs'])
      : undefined,
    tags: normalizeTags(meta.tags),
    nodes: nodes.map((node, index) => ({
      id: node.id,
      nodeType: node.type ?? 'action',
      configJson: (node.data ?? {}) as Record<string, unknown>,
      orderIndex: index,
    })),
    edges: edges.map((edge) => ({
      id: edge.id,
      sourceNodeId: edge.source,
      targetNodeId: edge.target,
      edgeType: edge.edgeType ?? edge.type ?? 'default',
    })),
  };
}
